/**
 * Thẻ dùng chung cho danh sách CHỈ ĐỌC trên điện thoại và iPad (14/09/2026) — Figma "Nạp thẻ trên ERP - Responsive"
 * (node `20:5653`): danh sách đơn, giao dịch trong drawer, thành viên, lịch sử ví, mã thẻ đã mua.
 *
 * Bảng NHẬP LIỆU bước 2 KHÔNG dùng file này — thẻ của chúng dựng từ chính `columns` của bảng
 * (`components/topupWizard/TopupRowCards.tsx`) để luật báo lỗi không bị nhân đôi.
 *
 * Một cột hay hai cột là việc của CSS (`styles/common/_mobileCard.scss`): điện thoại một cột, iPad hai cột.
 */
import React from 'react'
import { Spin } from 'antd'

import { MOBILE_LIST_EMPTY_TEXT } from '../../constants/responsive'

export interface MobileCardListProps<T> {
  items: readonly T[]
  itemKey: (item: T) => string
  renderItem: (item: T, index: number) => React.ReactNode
  /** Đang tải — phủ vòng quay lên danh sách hiện có; danh sách rỗng lúc đang tải thì KHÔNG nói "Không có dữ liệu". */
  loading?: boolean
  className?: string
  ariaLabel?: string
  emptyText?: React.ReactNode
}

export function MobileCardList<T>({
  items, itemKey, renderItem, loading = false, className, ariaLabel, emptyText = MOBILE_LIST_EMPTY_TEXT,
}: MobileCardListProps<T>): JSX.Element {
  return (
    <Spin spinning={loading}>
      {items.length === 0 ? (
        <p className="mobile-card-list__empty">{loading ? null : emptyText}</p>
      ) : (
        <ul className={className === undefined ? 'mobile-card-list' : `mobile-card-list ${className}`} aria-label={ariaLabel}>
          {items.map((item, index) => (
            <li key={itemKey(item)} className="mobile-card-list__item">
              {renderItem(item, index)}
            </li>
          ))}
        </ul>
      )}
    </Spin>
  )
}

export interface MobileCardProps {
  title?: React.ReactNode
  /** Góc phải đầu thẻ — huy hiệu trạng thái, nút Copy. */
  aside?: React.ReactNode
  /** Vạch ngăn dưới đầu thẻ (Figma vẽ ở danh sách đơn/thành viên/lịch sử ví). */
  divided?: boolean
  /** Cả thẻ là vùng bấm (mở chi tiết) — thay cho `onRow.onClick` của bảng desktop. */
  onOpen?: () => void
  /** Tên đọc cho trình đọc màn hình khi thẻ bấm được. */
  openLabel?: string
  className?: string
  children?: React.ReactNode
  /**
   * Hàng hành động cuối thẻ (vd nút Duyệt/Từ chối) — nằm NGOÀI vùng bấm mở thẻ. Có cả `onOpen` lẫn `footer` thì gốc là khung
   * thường, con `mobile-card__open` mang role=button bọc đầu thẻ + thân, `mobile-card__footer` theo sau — nút không lồng trong
   * phần tử role=button (axe `nested-interactive`; VoiceOver/WebKit có thể ẩn nút lồng). Không có `footer` ⇒ DOM y như cũ.
   */
  footer?: React.ReactNode
}

/** Phần tử có việc riêng khi bấm — nằm trong thẻ bấm được thì cú bấm là của nó, không mở thẻ. */
const INNER_INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"]'

export const MobileCard: React.FC<MobileCardProps> = ({
  title, aside, divided = false, onOpen, openLabel, className, children, footer,
}) => {
  // Bấm được + có footer ⇒ vùng bấm là con `__open`, không phải gốc (xem JSDoc `footer`).
  const openArea = onOpen !== undefined && footer !== undefined
  const classes = ['mobile-card']
  if (divided) classes.push('mobile-card--divided')
  if (onOpen !== undefined && !openArea) classes.push('mobile-card--clickable')
  if (className !== undefined) classes.push(className)

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
    // Enter/Space trên nút BÊN TRONG thẻ (Copy, Hoàn điểm) là việc của nút đó, không phải mở thẻ.
    if (onOpen === undefined || event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onOpen()
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (onOpen === undefined) return
    // Cùng luật với bàn phím: bấm nút/link BÊN TRONG thẻ không mở thẻ — chỉ mở khi phần tử tương tác gần nhất là chính thẻ.
    const interactive = event.target instanceof Element ? event.target.closest(INNER_INTERACTIVE_SELECTOR) : null
    if (interactive !== null && interactive !== event.currentTarget) return
    onOpen()
  }

  // Thẻ chỉ trở thành vùng bấm được khi có `onOpen` — gộp props tương tác vào một object rồi spread, để
  // `<div>` không mang `role`/`tabIndex` "lơ lửng" khi thẻ chỉ đọc (đúng test: role phải là `null`).
  const interactiveProps: React.HTMLAttributes<HTMLDivElement> = onOpen === undefined ? {} : {
    role: 'button',
    tabIndex: 0,
    'aria-label': openLabel,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
  }

  const body = (
    <>
      {(title !== undefined || aside !== undefined) && (
        <div className="mobile-card__head">
          <div className="mobile-card__title">{title}</div>
          {aside !== undefined && <div className="mobile-card__aside">{aside}</div>}
        </div>
      )}
      {children}
    </>
  )
  const footerRow = footer !== undefined && <div className="mobile-card__footer">{footer}</div>

  if (openArea) {
    return (
      <div className={classes.join(' ')}>
        <div className="mobile-card__open" {...interactiveProps}>{body}</div>
        {footerRow}
      </div>
    )
  }

  return (
    <div className={classes.join(' ')} {...interactiveProps}>
      {body}
      {footerRow}
    </div>
  )
}

export const MobileCardFields: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dl className="mobile-card__fields">{children}</dl>
)

export interface MobileCardFieldProps {
  label: React.ReactNode
  /** Chiếm cả hai cột — nội dung dài (chi tiết giao dịch, câu lỗi). */
  wide?: boolean
  children: React.ReactNode
}

export const MobileCardField: React.FC<MobileCardFieldProps> = ({ label, wide = false, children }) => (
  <div className={wide ? 'mobile-card__field mobile-card__field--wide' : 'mobile-card__field'}>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
)
